import { AspectRatio } from "@radix-ui/react-aspect-ratio";
import { type LinksFunction, type MetaFunction } from "@remix-run/node";
import { Link } from "@remix-run/react";
import { useState } from "react";
import andreOnMacBookPro from "#app/routes/_marketing+/images/andre-on-macbook-pro.webp";
import andreSquared from "#app/routes/_marketing+/images/andre_casal_squared.webp";
import signatureBlack from "#app/routes/_marketing+/images/signature-black.webp";
import signatureWhite from "#app/routes/_marketing+/images/signature-white.webp";
import apo from "#app/routes/_marketing+/photos/apo-zadeh.webp";
import catia from "#app/routes/_marketing+/photos/catia-silva.webp";
import daniela from "#app/routes/_marketing+/photos/daniela-alexandra.webp";
import dejan from "#app/routes/_marketing+/photos/dejan-milosevic.webp";
import fabiana from "#app/routes/_marketing+/photos/fabiana-milanez.webp";
import goncalo from "#app/routes/_marketing+/photos/goncalo-barreiros.webp";
import ines from "#app/routes/_marketing+/photos/ines-barreiros.webp";
import leah from "#app/routes/_marketing+/photos/leah-meirinhos.webp";
import miguel from "#app/routes/_marketing+/photos/miguel-ferreira.webp";
import paulo from "#app/routes/_marketing+/photos/paulo-jorge.webp";
import tiago from "#app/routes/_marketing+/photos/tiago-da-silva-coelho.webp";
import BackgroundBlur from "#app/ui/components/backgrounds/bg-blur.js";
import BackgroundDiagonal from "#app/ui/components/backgrounds/bg-diagonal.js";
import BackgroundSquareLines from "#app/ui/components/backgrounds/bg-square-lines.js";
import { Icon } from "#app/ui/components/custom/icon.js";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "#app/ui/components/shadcnui/accordion.js";
import {
  AvatarGroup,
  AvatarGroupList,
  Avatar,
  AvatarImage,
  AvatarFallback,
} from "#app/ui/components/shadcnui/avatar.js";
import { Button } from "#app/ui/components/shadcnui/button.js";
import { Container } from "#app/ui/components/verveui/layout/container.js";
import { Flex } from "#app/ui/components/verveui/layout/flex.js";
import { H1 } from "#app/ui/components/verveui/typography/h1.js";
import { H2 } from "#app/ui/components/verveui/typography/h2.js";
import { H3 } from "#app/ui/components/verveui/typography/h3.js";
import { Lead } from "#app/ui/components/verveui/typography/lead.js";
import { P } from "#app/ui/components/verveui/typography/p.js";

export const meta: MetaFunction = () => {
  return [
    { title: "Explicações de Programação — André Casal" },
    {
      name: "description",
      content:
        "800+ alunos. 20 anos de experiência. 5 estrelas. Explicações de Ciência da Computação para universitários que querem dominar a matéria.",
    },
    {
      name: "keywords",
      content:
        "explicações de programação, explicador de programação, tutoria de programação, tutor de programação, professor particular de programação, boas notas programação, como estudar programação",
    },
  ];
};

export const links: LinksFunction = () => {
  return [
    {
      rel: "preload",
      href: andreSquared,
      as: "image",
    },
    {
      rel: "preload",
      href: daniela,
      as: "image",
    },
    {
      rel: "preload",
      href: goncalo,
      as: "image",
    },
    {
      rel: "preload",
      href: miguel,
      as: "image",
    },
    {
      rel: "preload",
      href: paulo,
      as: "image",
    },
    {
      rel: "preload",
      href: dejan,
      as: "image",
    },
    {
      rel: "canonical",
      href: "https://estuda-comigo.com/",
    },
  ];
};

const Route = () => {
  return (
    <>
      <Hero />
      <Problem />
      <Solution />
      <About />
      <Testimonials />
      <Container>
        <div className="flex flex-col items-center py-12">
          <P className="text-muted-500">Pronto para ter excelentes notas?</P>
          <Link
            to="#pricing"
            className="text-size-sm hover:bg-brand/90 focus-visible:ring-ring mt-4 inline-flex h-12 items-center justify-center whitespace-nowrap rounded-md bg-brand px-10 font-semibold text-muted-50 shadow ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 dark:text-white"
          >
            Ver planos
          </Link>
        </div>
      </Container>
      <Features />
      <Pricing />
      <FAQ />
      <Container className="pt-14">
        <div className="py-24 sm:py-32 lg:flex lg:items-center lg:gap-x-10 lg:py-40">
          <div className="mx-auto max-w-2xl lg:mx-0 lg:flex-auto">
            <H2 className="mt-10 max-w-lg text-muted-900">Uma nota para ti</H2>
            <P className="mt-6 text-muted-600">
              Obrigado por teres lido até aqui. É uma honra ter tantos alunos
              que confiam em mim para os ajudar a encontrar uma maneira melhor
              de frequentar a universidade.
            </P>
            <P className="mt-6 text-muted-600">Ao teu sucesso! 🥂</P>
          </div>
          <div className="mt-16 rounded-xl p-8 sm:mt-24 lg:mt-0">
            <img
              width="228"
              height="85"
              src={signatureBlack}
              alt="Assinatura de André Casal"
              className="mt-16 dark:hidden"
            />
            <img
              width="228"
              height="85"
              src={signatureWhite}
              alt="Assinatura de André Casal"
              className="mt-16 hidden dark:block"
            />
          </div>
        </div>
      </Container>
    </>
  );
};
export default Route;

const WEEKS_PER_MONTH = 4;

const plans = [
  {
    name: "Manutenção",
    hoursPerWeek: 2,
    price: 400,
    popular: false,
    description:
      "Para estudantes que acompanham a matéria mas precisam de <strong>apoio regular</strong> para consolidar.",
    stripeLink: "https://buy.stripe.com/5kQ7sL9PL8bP74790T6sw0a",
  },
  {
    name: "Recomendado",
    hoursPerWeek: 3,
    price: 600,
    popular: true,
    description:
      "O plano mais escolhido. Ideal para quem quer <strong>dominar a matéria</strong> e ter excelentes notas.",
    stripeLink: "https://buy.stripe.com/28EdR91jfajXdsv6SL6sw0b",
  },
  {
    name: "Intensivo",
    hoursPerWeek: 4,
    price: 800,
    popular: false,
    description:
      "Para épocas de exames ou cadeiras difíceis que exigem <strong>preparação intensiva</strong>.",
    stripeLink: "https://buy.stripe.com/9B628rbXTfEhcordh96sw0c",
  },
];

function pricePerHour(plan: (typeof plans)[number]): number {
  return plan.price / (plan.hoursPerWeek * WEEKS_PER_MONTH);
}

const minPricePerHour = Math.min(...plans.map(pricePerHour));

const Hero = () => {
  return (
    <Container className="sm:mt-20">
      <div className="space-y-10 sm:grid sm:grid-cols-2 sm:gap-10 sm:space-y-0">
        <div>
          <H1 className="text-center">De reprovado dois anos seguidos a 16.</H1>
          <Lead className="text-center dark:text-muted-800">
            800+ alunos. 20 anos de experiência. 5 estrelas.
          </Lead>
          <p className="mb-2 text-center text-lg font-semibold text-muted-600">
            A partir de {minPricePerHour}€/hora · Agenda limitada
          </p>
          <Link
            to="#pricing"
            className="text-size-sm hover:bg-brand/90 focus-visible:ring-ring mt-4 inline-flex h-14 w-full items-center justify-center whitespace-nowrap rounded-md bg-brand px-10 font-semibold text-muted-50 shadow ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 dark:text-white"
          >
            Ver planos
          </Link>
          <Flex justify="center" items="start" gap="6" className="pt-10">
            <AvatarGroup className="flex">
              <AvatarGroupList>
                <Avatar className="border-2">
                  <AvatarImage src={daniela} alt="Profile" />
                  <AvatarFallback>LF</AvatarFallback>
                </Avatar>
                <Avatar className="border-2">
                  <AvatarImage src={goncalo} alt="Profile" />
                  <AvatarFallback>LF</AvatarFallback>
                </Avatar>
                <Avatar className="border-2">
                  <AvatarImage src={miguel} alt="Profile" />
                  <AvatarFallback>LF</AvatarFallback>
                </Avatar>
                <Avatar className="border-2">
                  <AvatarImage src={paulo} alt="Profile" />
                  <AvatarFallback>LF</AvatarFallback>
                </Avatar>
                <Avatar className="border-2">
                  <AvatarImage src={dejan} alt="Profile" />
                  <AvatarFallback>LF</AvatarFallback>
                </Avatar>
              </AvatarGroupList>
            </AvatarGroup>
            <Flex orientation="vertical" gap="2">
              <Flex className="text-xl">
                <Icon name="star-filled" className="text-yellow-500" />
                <Icon name="star-filled" className="text-yellow-500" />
                <Icon name="star-filled" className="text-yellow-500" />
                <Icon name="star-filled" className="text-yellow-500" />
                <Icon name="star-filled" className="text-yellow-500" />
              </Flex>
              <p className="mb-20 text-left text-xs text-muted-600 dark:text-muted-700">
                Avaliação de 800+ alunos
              </p>
            </Flex>
          </Flex>
        </div>
        <div>
          <img
            height="292"
            width="292"
            className="w-full rounded-2xl border border-muted-200 bg-muted-50 object-cover"
            src={andreSquared}
            alt="André, o teu tutor de programação"
          />
          <p className="mx-auto text-center text-sm text-muted-600 dark:text-muted-700">
            Professor André Casal
          </p>
        </div>
      </div>
    </Container>
  );
};

const Problem = () => {
  return (
    <section className="sm:mt-20">
      <Container>
        <div className="mx-auto max-w-4xl text-center dark:text-background">
          <H2>Porque é que a programação é tão difícil?</H2>
          <Lead className="text-muted-600">
            Desafios que estudantes como tu enfrentam todos os dias.
          </Lead>
          <ul className="mx-auto max-w-md rounded-lg bg-red-50 p-4 text-left">
            <li className="flex items-center gap-2 sm:gap-4">
              <span className="flex h-4 w-4 items-center justify-center text-red-600">
                <Icon name="brain" />
              </span>{" "}
              Conceitos complexos sem orientação
            </li>
            <li className="flex items-center gap-2 sm:gap-4">
              +{" "}
              <span className="flex h-4 w-4 items-center justify-center text-red-600">
                <Icon name="puzzle" />
              </span>{" "}
              Exercícios que não consegues resolver sozinho
            </li>
            <li className="flex items-center gap-2 sm:gap-4">
              +{" "}
              <span className="flex h-4 w-4 items-center justify-center text-red-600">
                <Icon name="hourglass" />
              </span>{" "}
              Pouco tempo, muita matéria
            </li>
            <li className="flex items-center gap-2 sm:gap-4">
              +{" "}
              <span className="flex h-4 w-4 items-center justify-center text-red-600">
                <Icon name="crumpled-paper" />
              </span>{" "}
              Ansiedade nos exames
            </li>
            <li className="mt-6 flex items-center gap-2 sm:gap-4">
              ={" "}
              <span className="flex h-4 w-4 items-center justify-center text-red-700">
                <Icon name="angry" />
              </span>{" "}
              <b className="text-red-700">
                Más notas, stress, perda de oportunidades
              </b>
            </li>
          </ul>
          <p className="mt-10 text-gray-400">
            Há uma forma melhor <Icon name="arrow-down" />
          </p>
        </div>
      </Container>
    </section>
  );
};

const Solution = () => {
  return (
    <Container className="pb-20 pt-12">
      <H2 className="mt-2 text-center text-muted-900">
        O que muda com as explicações
      </H2>
      <ul className="mx-auto mt-10 flex max-w-md flex-col rounded-lg bg-green-50 p-4 text-base">
        <li className="flex items-center gap-x-3 font-semibold leading-7 text-green-700">
          <Icon
            name="check"
            className="h-5 w-5 flex-none text-green-700"
            aria-hidden="true"
          />
          Explicações passo-a-passo, ao teu ritmo
        </li>
        <li className="flex items-center gap-x-3 font-semibold leading-7 text-green-700">
          <Icon
            name="check"
            className="h-5 w-5 flex-none text-green-700"
            aria-hidden="true"
          />
          Exercícios práticos com cenários reais
        </li>
        <li className="flex items-center gap-x-3 font-semibold leading-7 text-green-700">
          <Icon
            name="check"
            className="h-5 w-5 flex-none text-green-700"
            aria-hidden="true"
          />
          Técnicas de resolução de problemas
        </li>
        <li className="flex items-center gap-x-3 font-semibold leading-7 text-green-700">
          <Icon
            name="check"
            className="h-5 w-5 flex-none text-green-700"
            aria-hidden="true"
          />
          Preparação e confiança para os exames
        </li>
        <li className="mt-6 flex items-center gap-2 sm:gap-4">
          ={" "}
          <span className="flex h-4 w-4 items-center justify-center text-green-700">
            <Icon name="medal" />
          </span>{" "}
          <b className="text-green-700">
            Excelentes notas, orgulho, oportunidades
          </b>
        </li>
      </ul>
    </Container>
  );
};

const About = () => {
  return (
    <BackgroundDiagonal>
      <Container className="py-20">
        <div className="lg:flex lg:gap-x-10">
          <div className="mx-auto max-w-2xl lg:mx-0 lg:flex-auto">
            <H2>
              Sobre o teu professor <Icon name="medal" />
            </H2>
            <P className="mt-6 text-muted-700">
              Dou aulas de Ciência da Computação há mais de{" "}
              {new Date().getFullYear() - 2006} anos e já ajudei centenas de
              alunos a obterem excelentes notas. Também trabalhei como
              engenheiro de software em empresas como a Fundação Calouste
              Gulbenkian, a rede de televisão americana NBC e a marca de bebidas
              Monster Energy. Atualmente dou formação a alunos
              universitários e engenheiros de software.
            </P>
            <Flex justify="center" items="start" gap="6" className="pt-10">
              <AvatarGroup className="flex">
                <AvatarGroupList>
                  <Avatar className="border-2">
                    <AvatarImage src={apo} alt="Profile" />
                    <AvatarFallback>LF</AvatarFallback>
                  </Avatar>
                  <Avatar className="border-2">
                    <AvatarImage src={catia} alt="Profile" />
                    <AvatarFallback>LF</AvatarFallback>
                  </Avatar>
                  <Avatar className="border-2">
                    <AvatarImage src={ines} alt="Profile" />
                    <AvatarFallback>LF</AvatarFallback>
                  </Avatar>
                  <Avatar className="border-2">
                    <AvatarImage src={fabiana} alt="Profile" />
                    <AvatarFallback>LF</AvatarFallback>
                  </Avatar>
                  <Avatar className="border-2">
                    <AvatarImage src={leah} alt="Profile" />
                    <AvatarFallback>LF</AvatarFallback>
                  </Avatar>
                </AvatarGroupList>
              </AvatarGroup>
              <Flex orientation="vertical" gap="2">
                <Flex className="text-xl">
                  <Icon name="star-filled" className="text-yellow-500" />
                  <Icon name="star-filled" className="text-yellow-500" />
                  <Icon name="star-filled" className="text-yellow-500" />
                  <Icon name="star-filled" className="text-yellow-500" />
                  <Icon name="star-filled" className="text-yellow-500" />
                </Flex>
                <p className="mb-20 text-left text-xs text-muted-600 dark:text-muted-700">
                  Confiado por 800+ universitários
                </p>
              </Flex>
            </Flex>
          </div>
          <div>
            <img
              height="163"
              width="292"
              className="w-full rounded-2xl border border-muted-200 bg-muted-50 object-cover"
              src={andreOnMacBookPro}
              alt="André em explicação"
            />
            <p className="mx-auto mt-2 text-center text-sm text-muted-600">
              Isto é o que vês quando estás a ter uma explicação comigo!
            </p>
          </div>
        </div>
      </Container>
    </BackgroundDiagonal>
  );
};

const Testimonials = () => {
  const testimonials = [
    [
      [
        {
          body: "Antes de começar a ser orientado pelo André, eu odiava Assembly e Microprocessadores. Eu estava a repetir a disciplina pela terceira vez e senti que ia reprovar mais uma vez. Mas depois que comecei a ser orientado pelo André, comecei a gostar do que estava a aprender, porque comecei a entender a matéria e até ficou divertido! Pela primeira vez, senti-me confiante! O André é muito simpático e paciente: ele desenhava diagramas, explicava, e usava outras linguagens de programação para me ajudar a ver a ligação entre linguagens de alto nível e Assembly, e tudo estava ótimo! Nunca tive um professor assim. Quando o resultado do exame chegou, fiquei perplexo ao ver que havia ido de reprovado nessa matéria por dois anos consecutivos para ter um 16! O método de ensino e a dedicação do André são, na minha opinião, imbatíveis.",
          author: {
            name: "Gonçalo Barreiros",
            imageUrl: goncalo,
          },
        },
        {
          body: "Eu não conseguia entender a matéria da aula que estava a ser dada pelos professores. Eles não estavam a fazer um bom trabalho e não nos davam os recursos necessários. Tentei ir à biblioteca da universidade, tentei conversar com colegas que entendiam um pouco mais da aula mas não adiantou muito. A diferença [entre ter e não ter explicações com o André] é que eu não passaria nas provas. Tenho obtido resultados. Tem sido uma boa experiência, tenho aprendido e melhorado.",
          author: {
            name: "Miguel Ferreira",
            imageUrl: miguel,
          },
        },
        {
          body: "Excelente explicador!",
          author: {
            name: "Jose Maria",
          },
        },
      ],
      [
        {
          body: "Olá André! Espero que esteja tudo bem! Enquanto isso, as notas foram publicadas [...] A de programação foi muito melhor do que eu esperava! [Captura de tela com nota de 17] A programação acabou por ser a melhor ahah!",
          author: {
            name: "Guilherme Echeverri",
          },
        },
        {
          body: "É excelente ter explicações com o André. Entendo tudo!",
          author: {
            name: "Maria Isabel",
          },
        },
        {
          body: "Muito bom!",
          author: {
            name: "Wilson Mesquita",
          },
        },
        {
          body: "Além das expectativas!",
          author: {
            name: "Helena Oliveira",
          },
        },
        {
          body: "Um excelente explicador! Sabe como orientar passo-a-passo para resolver problemas. Experiência muito boa em geral.",
          author: {
            name: "Miguel Lomba",
          },
        },
      ],
    ],
    [
      [
        {
          body: "Comecei praticamente do zero e senti uma grande evolução graças à ajuda do André. Explica a matéria de forma simples e clara, como se a estivéssemos a aprender pela primeira vez, e tem sempre a paciência necessária para esclarecer todas as dúvidas. Quando alguma explicação não resulta, procura outra abordagem até conseguirmos perceber. Além disso, adapta a forma de ensinar às necessidades de cada aluno, o que torna a aprendizagem muito mais fácil e eficaz. Recomendo a qualquer pessoa que procure um explicador dedicado, disponível e genuinamente interessado no sucesso dos seus alunos.",
          author: {
            name: "Tiago da Silva Coelho",
            imageUrl: tiago,
          },
        },
        {
          body: "Fiquei com a impressão de que 2h era pouco tempo, e que o Prof. André poderia ter-nos ajudado mais, não fossem os exercícios da minha faculdade serem supostamente pouco convencionais.",
          author: {
            name: "Henrique Silvestre",
          },
        },
        {
          body: "É perfeito. Recomendo 100%. Não poderia ter encontrado melhor. Muito grato por tudo.",
          author: {
            name: "Isabel Rodrigues",
          },
        },
        {
          body: "Profissional: competência e disponibilidade.",
          author: {
            name: "Jaime Torrinhas",
          },
        },
        {
          body: "Ajudou muito, foi muito paciente e interessado em ensinar e explicar o assunto.",
          author: {
            name: "Isabel Bozzato",
          },
        },
      ],
      [
        {
          body: "Os meus professores introduziram a programação do Arduino com pequenos blocos de código. Um pouco de código aqui e ali. Mas eles nunca explicaram as coisas muito bem. Eles presumiam que íamos procurar as peças que faltavam. Tentei pesquisar na internet e conversar com colegas, mas não consegui encontrar bons recursos na internet e meus amigos estavam tão perdidos quanto eu. Quando comecei as aulas de reforço com o André, minhas notas aumentaram exponencialmente. Estou com o André há um ano neste momento, a disciplina atual é a segunda que estamos a fazer juntos. Na primeira aula, se eu sei alguma coisa sobre Arduino, foi porque o André me ensinou. Quero dizer, o André não apenas me ensinou como a fazer projetos com Arduino, mas também me ensinou ao ponto de eu poder concluir os exames sozinho! E em termos de disponibilidade o André é top.",
          author: {
            name: "Paulo Jorge",
            imageUrl: paulo,
          },
        },
        {
          body: "André Casal é o explicador mais inteligente, trabalhador e atencioso que conheço. Se deseja receber ajuda do mais alto nível, trabalhe com ele!",
          author: {
            name: "Mony Chhim",
          },
        },
        {
          body: "Estou muito grata por toda a ajuda: conhecimento, disponibilidade, paciência e, acima de tudo, pela gentileza. Obrigado!",
          author: {
            name: "Maria Ribeiro",
          },
        },
        {
          body: "O André foi muito paciente comigo e explicou tudo muito bem. Se eu precisar de ajuda novamente... é com ele.",
          author: {
            name: "Marcos Marcos",
          },
        },
      ],
    ],
  ];

  function classNames(...classes: string[]) {
    return classes.filter(Boolean).join(" ");
  }

  const [isExpanded, setIsExpanded] = useState(false);

  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <BackgroundBlur>
      <Container>
        <div className="py-32">
          <div className="mx-auto max-w-2xl text-center">
            <H2 className="mt-2 text-center">Testemunhos de grandes alunos</H2>
          </div>
          <div className="mx-auto mt-16 grid max-w-2xl grid-cols-1 grid-rows-1 gap-8 text-sm leading-6 text-muted-900 sm:mt-20 sm:grid-cols-2 xl:mx-0 xl:max-w-none xl:grid-flow-col xl:grid-cols-4">
            <figure className="ring-muted-900/5 col-span-1 overflow-hidden rounded-2xl bg-background shadow-lg ring-1 sm:col-span-2 xl:col-start-2 xl:row-end-1">
              <AspectRatio ratio={16 / 9} className="relative">
                <iframe
                  loading="lazy"
                  className="absolute inset-0 h-full w-full"
                  src="https://www.youtube.com/embed/lwNh4Ddtl3U?modestbranding=1&rel=0"
                  title="YouTube video player"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              </AspectRatio>
              <figcaption className="border-muted-900/10 flex items-center gap-x-4 border-t px-6 py-4">
                <img
                  className="h-10 w-10 flex-none rounded-full bg-muted-50"
                  src={daniela}
                  alt="Daniela Alexandra"
                />
                <div className="flex-auto">
                  <p>Daniela Alexandra</p>
                  <div className="flex">
                    {Array(5)
                      .fill(null)
                      .map((_value, i) => (
                        <Icon
                          key={i}
                          name="star-filled"
                          className="text-size-lg text-brand text-yellow-400"
                        />
                      ))}
                  </div>
                </div>
              </figcaption>
            </figure>
            {testimonials.map((columnGroup, columnGroupIdx) => (
              <div
                key={columnGroupIdx}
                className="space-y-8 xl:contents xl:space-y-0"
              >
                {columnGroup.map((column, columnIdx) => (
                  <div
                    key={columnIdx}
                    className={classNames(
                      (columnGroupIdx === 0 && columnIdx === 0) ||
                        (columnGroupIdx === testimonials.length - 1 &&
                          columnIdx === columnGroup.length - 1)
                        ? "xl:row-span-2"
                        : "xl:row-start-1",
                      "space-y-8",
                    )}
                  >
                    {column
                      .slice(0, isExpanded ? Infinity : 1)
                      .map((testimonial, i) => (
                        <figure
                          key={i}
                          className="ring-muted-900/5 overflow-hidden rounded-2xl bg-background p-6 shadow-lg ring-1"
                        >
                          <blockquote>
                            <P className="text-muted-900">{`“${testimonial.body}”`}</P>
                          </blockquote>
                          <figcaption className="mt-6 flex items-center gap-x-4">
                            {"imageUrl" in testimonial.author ? (
                              <img
                                className="h-10 w-10 rounded-full bg-muted-50"
                                src={testimonial.author.imageUrl}
                                alt={testimonial.author.name}
                              />
                            ) : null}
                            <div>
                              <p>{testimonial.author.name}</p>
                              <div className="flex">
                                {Array(5)
                                  .fill(null)
                                  .map((_value, i) => (
                                    <Icon
                                      key={i}
                                      name="star-filled"
                                      className="text-size-lg text-brand text-yellow-400"
                                    />
                                  ))}
                              </div>
                            </div>
                          </figcaption>
                        </figure>
                      ))}
                  </div>
                ))}
              </div>
            ))}
          </div>
          <Button
            onClick={toggleExpand}
            variant="outline"
            className="mx-auto mt-16 flex w-full max-w-md items-center justify-center"
            aria-expanded={isExpanded}
            aria-controls="expandable-content"
          >
            {isExpanded ? (
              <>
                Mostrar menos{" "}
                <Icon name="chevron-up" className="ml-2 h-4 w-4" />
              </>
            ) : (
              <>
                Mostrar mais{" "}
                <Icon name="chevron-down" className="ml-2 h-4 w-4" />
              </>
            )}
          </Button>
        </div>
      </Container>
    </BackgroundBlur>
  );
};

const Features = () => {
  const subjects = {
    computerscience: [
      { name: "Sistemas Digitais" },
      { name: "Organização e Arquitetura de Computadores" },
      { name: "Assembly (x86, x86-64, ARM, MIPS)" },
      { name: "Sistemas Operativos" },
      { name: "Redes de Computadores" },
      { name: "Estruturas de dados" },
      { name: "Bases de dados (SQL, SQLite, MongoDB, etc)" },
      { name: "Algoritmos" },
      { name: "Assembly (x86, x64, MIPS, ARM, PEPE)" },
      { name: "C" },
      { name: "C++" },
      { name: "C#" },
      { name: "Java" },
      { name: "Kotlin" },
      { name: "Python" },
      { name: "JavaScript" },
      { name: "Browser, Node, Deno APIs" },
      { name: "TypeScript" },
      { name: "Rust" },
      { name: "HTML" },
      { name: "CSS" },
      { name: "React, Vue, Angular" },
      { name: "Express, Next, Remix" },
      { name: "REST and GraphQL APIs" },
    ],
  };
  return (
    <BackgroundSquareLines>
      <Container className="py-24 sm:py-32">
        <H2 className="mt-10 max-w-xl text-muted-900">
          As disciplinas que ensino
        </H2>
        <ul className="ml-6 mt-6 list-disc space-y-4 sm:columns-2 sm:gap-10 md:columns-3">
          {subjects.computerscience.map((item) => (
            <li key={item.name}>
              <span>{item.name}</span>
            </li>
          ))}
        </ul>
      </Container>
    </BackgroundSquareLines>
  );
};

const Pricing = () => {
  const includedFeatures = [
    "Estudar a par",
    "Esclarecer dúvidas",
    "Resolver desafios difíceis",
    "Ajuda com projetos",
    "Reserva de horário",
  ];


  return (
    <Container id="pricing">
      <div className="py-16 sm:py-24">
        <div className="mx-auto max-w-2xl sm:text-center">
          <H2 className="mt-2 text-center font-bold" id="pricing">
            Preços simples
          </H2>
          <P className="mx-auto mt-4 max-w-xl text-center text-muted-500">
            Subscrição mensal. Cancela a qualquer momento.
          </P>
        </div>
        {plans.map((plan) => (
          <div
            key={plan.name}
            className={`relative mx-auto mt-10 max-w-2xl rounded-3xl sm:mt-12 lg:mx-0 lg:flex lg:max-w-none ${plan.popular ? "ring-2 ring-brand" : "ring-1 ring-muted-200"}`}
          >
            {plan.popular ? (
              <div className="absolute -top-3 left-8 rounded-full bg-brand px-4 py-1 text-xs font-semibold text-white">
                Mais popular
              </div>
            ) : null}
            <div className="p-8 sm:p-10 lg:flex-auto">
              <H3>{plan.name} — {plan.hoursPerWeek}h/semana</H3>
              <P
                className="mt-4 text-muted-600"
                dangerouslySetInnerHTML={{ __html: plan.description }}
              />
              <ul className="mt-6 grid grid-cols-1 gap-3 text-sm leading-6 text-muted-600 sm:grid-cols-2 sm:gap-4">
                {includedFeatures.map((feature) => (
                  <li key={feature} className="flex gap-x-3">
                    <Icon
                      name="check"
                      className="h-6 w-5 flex-none text-brand"
                      aria-hidden="true"
                    />
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
            <div className="-mt-2 p-2 lg:mt-0 lg:w-full lg:max-w-md lg:flex-shrink-0">
              <div className="ring-muted-900/5 h-full rounded-2xl bg-muted-50 py-10 text-center ring-1 ring-inset lg:flex lg:flex-col lg:justify-center lg:py-16">
                <div className="mx-auto max-w-xs px-8">
                  <P className="mt-6 flex items-baseline justify-center">
                    <span className="text-6xl font-bold">{plan.price}</span>
                    <span className="tracking-wide text-muted-600">
                      EUR/mês
                    </span>
                  </P>
                  <p className="mt-1 text-sm text-muted-600">
                    {pricePerHour(plan)}€/hora
                  </p>
                  <Button asChild className="mt-8">
                    <a
                      href={plan.stripeLink}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Quero assinar este plano
                    </a>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </Container>
  );
};

const FAQ = () => {
  const faqs = [
    {
      question: "As explicações privadas não são demasiado caras?",
      answer:
        "Compreendo que o custo é uma preocupação, mas vale a pena considerar que este é um investimento no futuro académico e profissional do aluno. As minhas explicações são adaptadas às necessidades específicas de cada aluno, ajudando a melhorar as notas e a confiança em áreas complexas. Ofereço pacotes e planos flexíveis para se ajustarem ao vosso orçamento, e uma sessão inicial gratuita para que possam ver o valor antes de se comprometerem.",
    },
    {
      question: "Como posso ter a certeza de que o professor é realmente bom?",
      answer:
        "É normal ter dúvidas. Tenho mais de 20 anos de experiência em engenharia de software e ensino, e já trabalhei com empresas como a Fundação Gulbenkian, NBC e Monster Energy. Mais de 800 alunos passaram pelas minhas aulas, muitos com resultados dramáticos — como ir de reprovado a 16 numa disciplina. Os testemunhos falam por si.",
    },
    {
      question: "E se o meu filho não se adaptar ao professor?",
      answer:
        "A compatibilidade entre o tutor e o aluno é essencial para o sucesso. Por isso, a primeira sessão é sempre uma oportunidade para conhecermos o aluno e ajustarmos a metodologia ao seu estilo de aprendizagem. O meu foco é criar um ambiente de ensino seguro e positivo onde o aluno se sinta à vontade para aprender e fazer perguntas.",
    },
    {
      question: "Os horários das explicações não são convenientes para nós.",
      answer:
        "Sei que o tempo é um recurso precioso. Por isso, ofereço explicações online e horários flexíveis, incluindo fins de semana e períodos após o horário escolar. Assim, podem ajustar as explicações ao vosso ritmo e agenda, sem a pressão dos horários fixos.",
    },
    {
      question:
        "Será que o meu filho realmente precisa de explicações privadas?",
      answer:
        "Muitas vezes, os alunos não percebem quanto podem beneficiar de explicações personalizadas até experimentarem. Regra geral, se está a ler isto é porque já sentiu alguma dificuldade e está na altura de ter explicações. Não se trata apenas de melhorar as notas, mas de ganhar confiança, habilidades de estudo e uma compreensão mais profunda das matérias.",
    },
    {
      question: "O meu filho vai tornar-se dependente do professor?",
      answer:
        "O objetivo das minhas explicações é capacitar o aluno a ser independente. Ensino técnicas de estudo, resolução de problemas e abordagem prática às matérias, para que o aluno desenvolva as suas competências e confiança. A minha função é capacitar o aluno e torná-lo independente, autónomo e confiante.",
    },
    {
      question: "As explicações online são tão eficazes quanto as presenciais?",
      answer:
        "As explicações online oferecem ferramentas incríveis que muitas vezes tornam a aprendizagem mais eficaz do que as presenciais, como o uso de quadros virtuais, partilha de ecrãs e gravações das sessões para revisão posterior. A grande maioria dos meus alunos preferem este formato pela conveniência e eficácia.",
    },
    {
      question:
        "Já tivemos más experiências com outros explicadores. Como será diferente?",
      answer:
        "Compreendo a hesitação. Muitos dos meus alunos vieram de experiências anteriores frustrantes. A diferença está na abordagem: desde o primeiro encontro, definimos objetivos claros e um plano ajustado às necessidades específicas do aluno. O acompanhamento é contínuo e personalizado. Os testemunhos dos meus alunos são a melhor prova.",
    },
    {
      question: "Como sei que as explicações vão realmente melhorar as notas?",
      answer:
        "Os resultados são a melhor prova do impacto das minhas explicações. Muitos alunos melhoraram significativamente as suas notas e desenvolveram um entendimento mais profundo das matérias. Partilho regularmente histórias de sucesso e feedback de alunos que alcançaram os seus objetivos académicos, para que possam ver o impacto real das minhas sessões.",
    },
  ];
  return (
    <Container className="py-10">
      <H2 className="mx-auto text-center">Perguntas frequentes</H2>
      <div className="mx-auto w-full max-w-3xl rounded-lg p-6 shadow">
        <Accordion type="single" collapsible className="w-full">
          {faqs.map((faq, index) => (
            <AccordionItem key={index} value={`item-${index}`}>
              <AccordionTrigger className="text-left">
                {faq.question}
              </AccordionTrigger>
              <AccordionContent>{faq.answer}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </Container>
  );
};
